const INLINE_MARKDOWN_PATTERN = /(\*\*[^*]+?\*\*|`[^`]+?`)/g;

const normalizeTableCells = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isMarkdownTableSeparator = (line) => {
  const cleaned = line.trim();
  if (!cleaned.includes("|")) return false;
  return /^[\s:-]+$/.test(cleaned.replace(/\|/g, "").trim());
};

const isTableStart = (lines, index) =>
  lines[index]?.includes("|") &&
  isMarkdownTableSeparator(lines[index + 1] || "");

const getUnorderedListItem = (line) => line.match(/^\s*[-*]\s+(.+)$/)?.[1];

const getOrderedListItem = (line) => line.match(/^\s*\d+[.)]\s+(.+)$/)?.[1];

const getHeading = (line) => {
  const match = line.match(/^\s*(#{1,6})\s+(.+)$/);
  if (!match) return null;

  return {
    level: match[1].length,
    content: match[2].trim(),
  };
};

const parseMarkdownTable = (lines, startIndex) => {
  const headers = normalizeTableCells(lines[startIndex] || "");
  if (headers.length < 2) return null;

  const rows = [];
  let cursor = startIndex + 2;

  while (cursor < lines.length) {
    const line = lines[cursor];
    if (!line || !line.includes("|")) break;

    const cells = normalizeTableCells(line);
    if (cells.length < headers.length) break;
    rows.push(cells.slice(0, headers.length));
    cursor += 1;
  }

  if (!rows.length) return null;

  return {
    block: {
      type: "table",
      headers,
      rows,
    },
    nextIndex: cursor,
  };
};

const parseList = (lines, startIndex, ordered = false) => {
  const items = [];
  let cursor = startIndex;
  const getItem = ordered ? getOrderedListItem : getUnorderedListItem;

  while (cursor < lines.length) {
    const item = getItem(lines[cursor]);
    if (!item) break;
    items.push(item.trim());
    cursor += 1;
  }

  return {
    block: {
      type: ordered ? "orderedList" : "unorderedList",
      items,
    },
    nextIndex: cursor,
  };
};

const parseParagraph = (lines, startIndex) => {
  const paragraphLines = [];
  let cursor = startIndex;

  while (cursor < lines.length) {
    const line = lines[cursor];
    if (!line.trim()) break;
    if (isTableStart(lines, cursor)) break;
    if (getUnorderedListItem(line) || getOrderedListItem(line)) break;

    paragraphLines.push(line);
    cursor += 1;
  }

  return {
    block: {
      type: "paragraph",
      content: paragraphLines.join("\n").trim(),
    },
    nextIndex: cursor,
  };
};

const parseMarkdownBlocks = (content) => {
  const lines = String(content || "").split("\n");
  const blocks = [];
  let cursor = 0;

  while (cursor < lines.length) {
    const line = lines[cursor];

    if (!line.trim()) {
      cursor += 1;
      continue;
    }

    if (isTableStart(lines, cursor)) {
      const table = parseMarkdownTable(lines, cursor);
      if (table) {
        blocks.push(table.block);
        cursor = table.nextIndex;
        continue;
      }
    }

    const heading = getHeading(line);
    if (heading) {
      blocks.push({
        type: "heading",
        ...heading,
      });
      cursor += 1;
      continue;
    }

    if (getUnorderedListItem(line)) {
      const list = parseList(lines, cursor, false);
      blocks.push(list.block);
      cursor = list.nextIndex;
      continue;
    }

    if (getOrderedListItem(line)) {
      const list = parseList(lines, cursor, true);
      blocks.push(list.block);
      cursor = list.nextIndex;
      continue;
    }

    const paragraph = parseParagraph(lines, cursor);
    if (paragraph.block.content) {
      blocks.push(paragraph.block);
    }
    cursor = Math.max(paragraph.nextIndex, cursor + 1);
  }

  return blocks;
};

const renderInlineMarkdown = (text) => {
  const parts = String(text || "").split(INLINE_MARKDOWN_PATTERN);

  return parts.map((part, index) => {
    if (!part) return null;

    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>;
    }

    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={`${part}-${index}`}
          className="bg-muted/80 rounded px-1 py-0.5 text-[0.9em]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }

    return part;
  });
};

const MarkdownTable = ({ block }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[28rem] text-left text-xs">
      <thead>
        <tr className="border-border/80 border-b">
          {block.headers.map((header, index) => (
            <th
              key={`${header}-${index}`}
              className="text-muted-foreground px-2 py-2 font-medium"
            >
              {renderInlineMarkdown(header)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.rows.map((row, rowIndex) => (
          <tr key={`${rowIndex}-${row.join("|")}`} className="border-b/40">
            {row.map((cell, cellIndex) => (
              <td key={`${rowIndex}-${cellIndex}`} className="px-2 py-2">
                {renderInlineMarkdown(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MarkdownBlock = ({ block }) => {
  if (block.type === "table") {
    return <MarkdownTable block={block} />;
  }

  if (block.type === "heading") {
    const sizeClass =
      block.level <= 2
        ? "text-base"
        : block.level === 3
          ? "text-sm"
          : "text-xs";

    return (
      <div className={`${sizeClass} pt-1 font-semibold tracking-tight`}>
        {renderInlineMarkdown(block.content)}
      </div>
    );
  }

  if (block.type === "unorderedList") {
    return (
      <ul className="list-disc space-y-1 pl-5">
        {block.items.map((item, index) => (
          <li key={`${item}-${index}`}>{renderInlineMarkdown(item)}</li>
        ))}
      </ul>
    );
  }

  if (block.type === "orderedList") {
    return (
      <ol className="list-decimal space-y-1 pl-5">
        {block.items.map((item, index) => (
          <li key={`${item}-${index}`}>{renderInlineMarkdown(item)}</li>
        ))}
      </ol>
    );
  }

  return (
    <p className="whitespace-pre-wrap leading-relaxed">
      {renderInlineMarkdown(block.content)}
    </p>
  );
};

export const AssistantMessageContent = ({ content }) => {
  const blocks = parseMarkdownBlocks(content);

  if (!blocks.length) {
    return null;
  }

  return (
    <div className="space-y-3">
      {blocks.map((block, index) => (
        <MarkdownBlock key={`${block.type}-${index}`} block={block} />
      ))}
    </div>
  );
};
